import { useContext, useEffect, useState } from "react";

import { SeasonContext } from "domain/contexts/SeasonContext";
import { GameRecord } from "../models/GameRecord";
import { GameType, RoomSource } from "../models/types";
import { fetchLeaderboardUseCase } from "../useCase/fetchLeaderboardUseCase";

interface FilterLeaderboard {
  readonly selectedGameType: GameType;
  readonly selectedRoomSource: RoomSource;
  readonly isDataFetchingInProgress: boolean;
  readonly error?: Error;
  readonly gameRecords: GameRecord[];
  readonly lastWeeksWinner: GameRecord | null;
  readonly isLoading: boolean;
}

export const useFilterLeaderboard = (props: {
  initialGameType: GameType;
  initialSource: RoomSource;
}) => {
  const seasonContext = useContext(SeasonContext);
  const [currentSeasonId, setCurrentSeasonId] = useState<number>();

  useEffect(() => {
    if (
      seasonContext.seasonId > 0 &&
      currentSeasonId != seasonContext.seasonId
    ) {
      setCurrentSeasonId(seasonContext.seasonId);
      fetchNewData({});
    }
  }, [seasonContext.seasonId]);

  const fetchNewData = (newFilter: {
    source?: RoomSource;
    gameType?: GameType;
  }) => {
    setState((oldState) => ({
      ...oldState,
      isLoading: true,
      error: undefined,
    }));
    fetchLeaderboardUseCase
      .fetchLeaderboard(
        newFilter.gameType ?? state.selectedGameType,
        newFilter.source ?? state.selectedRoomSource
      )
      .ifLeft((error) => {
        setState((state) => ({ ...state, error: error, isLoading: false }));
      })
      .ifRight((result) => {
        setState((oldState) => ({
          ...oldState,
          isLoading: false,
          lastWeeksWinner: result.lastWeekWinner,
          gameRecords: result.records,
        }));
      })
      .run();
  };

  const [state, setState] = useState<FilterLeaderboard>({
    gameRecords: [],
    selectedGameType: props.initialGameType,
    selectedRoomSource: props.initialSource,
    isDataFetchingInProgress: false,
    lastWeeksWinner: null,
    isLoading: false,
  });

  const changeRoomSource = (source: RoomSource) => {
    if (source === state.selectedRoomSource) {
      return;
    }

    setState((oldState) => ({ ...oldState, selectedRoomSource: source }));
    fetchNewData({ source: source });
  };

  const changeTypeAction = (gameType: GameType) => {
    if (gameType === state.selectedGameType) {
      return;
    }

    setState((oldState) => ({ ...oldState, selectedGameType: gameType }));
    fetchNewData({ gameType: gameType });
  };

  return {
    viewState: state,
    actions: {
      changeType: changeTypeAction,
      changeRoomSource: changeRoomSource,
    },
  };
};
